import React, { useEffect } from 'react';
import { FiArrowLeft, FiCheck, FiX } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';
// import { Button } from '../../components/ui/Button/Button'; // Not used
import { OrganizationsList } from '../../components/OrganizationManagement';
// import { UserManagementCard, UserInviteForm } from '../../components/UserManagement'; // Not used - redirects to TeamMembers
import { SessionsList } from '../../components/SessionManagement';
// import { UsageAnalytics } from '../../components/BillingUsage'; // Not used
import { ActivityLogTable } from '../../components/AuditLogging';
import { Badge } from '../../components/ui/Badge/Badge';
import styles from './AdminPages.module.css';

// Organizations Page
export function OrganizationsPage() {
  const { /* user */ } = useAuth(); // user not used in this function

  return (
    <div className={styles.adminPage}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <Link to="/admin" className={styles.backButton}>
            <FiArrowLeft />
            Back to Admin
          </Link>
          <h1>Organizations</h1>
          <p>Manage organizations, members, and settings</p>
        </div>
      </div>

      <div className={styles.pageContent}>
        <OrganizationsList />
      </div>
    </div>
  );
}

// Users Page - Redirects to Team Members
export function UsersPage() {
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect to team members page instead
    navigate('/admin/members', { replace: true });
  }, [navigate]);

  return (
    <div className={styles.adminPage}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <h1>Redirecting...</h1>
          <p>Redirecting to Team Members page</p>
        </div>
      </div>
    </div>
  );
}

// Permissions Page — read-only matrix of roles vs. permissions.
// The ROLE_MATRIX mirrors RolePermissions.get_role_permissions on the backend
// (see SEO-SaaS/services/auth/auth_models.py). Custom-role editing and per-user
// overrides are tracked in the Enterprise roadmap.
const ROLES: Array<{ key: 'owner' | 'admin' | 'member' | 'viewer'; label: string; description: string }> = [
  { key: 'owner', label: 'Owner', description: 'Full control, including billing and org deletion.' },
  { key: 'admin', label: 'Admin', description: 'Manage members, roles and org settings (no billing-level destructive actions).' },
  { key: 'member', label: 'Member', description: 'Use product features; read-only org access.' },
  { key: 'viewer', label: 'Viewer', description: 'Read-only access to org information.' },
];

const PERMISSION_GROUPS: Array<{
  title: string;
  permissions: Array<{ key: string; label: string; description: string; roles: Array<'owner' | 'admin' | 'member' | 'viewer'> }>;
}> = [
  {
    title: 'Organization',
    permissions: [
      { key: 'org:read', label: 'View organization', description: 'See organization details and member list.', roles: ['owner', 'admin', 'member', 'viewer'] },
      { key: 'org:update', label: 'Edit organization', description: 'Rename and update settings (name, billing email, features).', roles: ['owner', 'admin'] },
      { key: 'org:delete', label: 'Delete organization', description: 'Soft-delete the organization (30-day recovery).', roles: ['owner'] },
      { key: 'org:create', label: 'Create organization', description: 'Create new organizations under the same account.', roles: ['owner'] },
    ],
  },
  {
    title: 'Members',
    permissions: [
      { key: 'org:invite', label: 'Invite members', description: 'Send invitations and configure invitee role.', roles: ['owner', 'admin'] },
      { key: 'org:update_member', label: 'Change member roles', description: 'Promote/demote members between admin, member, and viewer. Only owners can assign the owner role.', roles: ['owner', 'admin'] },
      { key: 'org:remove_member', label: 'Remove members', description: 'Deactivate a member in this organization.', roles: ['owner', 'admin'] },
    ],
  },
  {
    title: 'API keys',
    permissions: [
      { key: 'api_key:create', label: 'Create API keys', description: 'Issue personal API keys for the organization.', roles: ['owner', 'admin', 'member'] },
      { key: 'api_key:read', label: 'View API keys', description: 'List existing API keys.', roles: ['owner', 'admin', 'member', 'viewer'] },
      { key: 'api_key:update', label: 'Update API keys', description: 'Rotate, rename or change API-key scopes.', roles: ['owner', 'admin', 'member'] },
      { key: 'api_key:delete', label: 'Delete API keys', description: 'Revoke API keys.', roles: ['owner', 'admin', 'member'] },
    ],
  },
  {
    title: 'Products',
    permissions: [
      { key: 'service:similarity', label: 'Similarity', description: 'Access the Similarity service.', roles: ['owner', 'admin', 'member'] },
      { key: 'service:briefing', label: 'Briefings', description: 'Access the Briefing service.', roles: ['owner', 'admin', 'member'] },
      { key: 'service:monitoring', label: 'Monitoring', description: 'Access the Monitoring service.', roles: ['owner', 'admin', 'member'] },
    ],
  },
];

export function PermissionsPage() {
  return (
    <div className={styles.adminPage}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <Link to="/admin" className={styles.backButton}>
            <FiArrowLeft />
            Back to Admin
          </Link>
          <h1>Roles &amp; Permissions</h1>
          <p>Reference matrix of what each role can do. Custom roles are part of the Enterprise roadmap.</p>
        </div>
      </div>

      <div className={styles.pageContent}>
        <div className={styles.rolesLegend}>
          {ROLES.map((role) => (
            <div key={role.key} className={styles.roleLegendItem}>
              <Badge variant={role.key === 'owner' ? 'primary' : role.key === 'admin' ? 'info' : 'secondary'}>
                {role.label}
              </Badge>
              <span>{role.description}</span>
            </div>
          ))}
        </div>

        <div className={styles.matrixWrapper}>
          <table className={styles.permissionMatrix}>
            <thead>
              <tr>
                <th className={styles.permissionHeader}>Permission</th>
                {ROLES.map((role) => (
                  <th key={role.key} className={styles.roleHeader}>{role.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PERMISSION_GROUPS.map((group) => (
                <React.Fragment key={group.title}>
                  <tr className={styles.groupRow}>
                    <td colSpan={ROLES.length + 1}>{group.title}</td>
                  </tr>
                  {group.permissions.map((perm) => (
                    <tr key={perm.key}>
                      <td>
                        <div className={styles.permissionName}>{perm.label}</div>
                        <div className={styles.permissionKey}>{perm.key}</div>
                        <div className={styles.permissionDescription}>{perm.description}</div>
                      </td>
                      {ROLES.map((role) => {
                        const allowed = perm.roles.includes(role.key);
                        return (
                          <td key={role.key} className={styles.permissionCell}>
                            {allowed ? (
                              <FiCheck className={styles.allowed} aria-label="Allowed" />
                            ) : (
                              <FiX className={styles.denied} aria-label="Not allowed" />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Sessions Page
export function SessionsPage() {
  const { currentOrganization } = useOrganization();

  return (
    <div className={styles.adminPage}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <Link to="/admin" className={styles.backButton}>
            <FiArrowLeft />
            Back to Admin
          </Link>
          <h1>Sessions</h1>
          <p>Monitor and manage user sessions</p>
        </div>
      </div>

      <div className={styles.pageContent}>
        <SessionsList organizationId={currentOrganization?.id || ''} />
      </div>
    </div>
  );
}

// Billing Page - Export from separate file
export { default as BillingPage } from './BillingUsage';

// Audit Page
export function AuditPage() {
  const { currentOrganization } = useOrganization();

  if (!currentOrganization?.id) {
    return (
      <div className={styles.adminPage}>
        <div className={styles.pageHeader}>
          <div className={styles.headerContent}>
            <Link to="/admin" className={styles.backButton}>
              <FiArrowLeft />
              Back to Admin
            </Link>
            <h1>Audit & Security</h1>
            <p>Unified activity log with security events and audit trails</p>
          </div>
        </div>
        <div className={styles.pageContent}>
          <div className={styles.loadingContainer}>
            <span>Loading organization...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.adminPage}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <Link to="/admin" className={styles.backButton}>
            <FiArrowLeft />
            Back to Admin
          </Link>
          <h1>Audit & Security</h1>
          <p>Unified activity log with security events and audit trails</p>
        </div>
      </div>

      <div className={styles.pageContent}>
        <ActivityLogTable organizationId={currentOrganization.id} />
      </div>
    </div>
  );
}